// src/components/Statistics.js — « Statistiques » (/statistics). What the
// support and its AI agent did over a period, every figure from ONE call,
// GET /api/statistics/overview (Backend statistics_service.
// get_comprehensive_stats: each section applies the same tenant, period,
// brand, client and carrier filters). Nothing here recounts tickets: tiles
// and rows read the API's own counts (statisticsModel.js only reshapes them).
// URL: ?client=<clients_edn id> (B2B client page), ?carrier=<name> (carrier
// pages), ?days=7|30|90|365|custom (+ start, end), ?brand=<id>, ?view=<tab>.
// Filter choices: GET /api/brands/list, /api/logistics/clients-edn,
// /api/statistics/carriers?limit=100.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  BarElement, CategoryScale, Chart as ChartJS, Legend as LegendPlugin, LinearScale, LineElement, PointElement, Tooltip,
} from 'chart.js';
import {
  Bot, Building2, ChartColumn, ChevronRight, Clock, Filter, Gauge, Inbox, RefreshCw, Smile, Sparkles,
  Tag, Timer, Truck, X,
} from 'lucide-react';
import {
  Badge, Banner, Card, DescriptionList, EmptyState, Kpi, Meter, PageHeader, Segmented, Tabs,
} from '../design/ui';
import { applyChartTheme, seriesColor, seriesWash } from '../design/charts';
import { getStatusClass, getStatusLabel, normalizeTicketStatus } from '../constants/ticketStatuses';
import {
  bucketSeries, clientIdFor, customRangeReady, fillDays, formatDecimal, formatNumber, formatPercent,
  granularityFor, joinDuration, matrixSlots, matrixValue, mergeBrandRows, mergeStatusRows, outlierCap,
  overviewQuery, readStatsParams, rollup, sentimentTone, splitHours, splitMinutes, toolsHistogram,
} from './statisticsModel';
import { formatShortDate } from '../utils/dates';
import './Statistics.css';

// Line, point and legend are registered because applyChartTheme sets their
// defaults (it throws when they are missing); this page draws bars and its
// legends in HTML (.u-legend), so each chart turns the canvas legend off.
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, LegendPlugin);

let chartThemed = false;
const ensureChartTheme = () => {
  if (chartThemed) return;
  applyChartTheme(ChartJS);
  chartThemed = true;
};

// Canvas colours come from the design tokens (« Autres » grey, segment gaps).
const cssToken = (name, fallback) => {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const TOOL_LABELS = {
  retrieve_knowledge: 'tools.retrieveKnowledge',
  assign_to_employee: 'tools.assignToEmployee',
  resolve_ticket: 'tools.resolveTicket',
  Close_ticket: 'tools.closeTicket',
  get_tracking_info: 'tools.getTrackingInfo',
  Retrieve_customer_order_by_reference_and_email: 'tools.retrieveOrder',
  Update_customer_shipping_address: 'tools.updateShippingAddress',
  find_relay_points: 'tools.findRelayPoints',
  create_return_with_edn_label: 'tools.createReturn',
  carriers_conversation: 'tools.carriersConversation',
  Retour: 'tools.return',
  fill_form_fields: 'tools.fillPDF',
  getOrderById: 'tools.getOrderById',
  carriers_conversation_update: 'tools.carriersConversationUpdate',
  order_status: 'tools.orderStatus',
  Request_return_order: 'tools.Request_return_order',
  Retrieve_customer_order_by_ID: 'tools.Retrieve_customer_order_by_ID',
  Request_exchange_shopify: 'tools.Request_exchange_shopify',
  Get_all_product_variants: 'tools.Get_all_product_variants',
  pass_response: 'tools.pass_response',
  change_ticket_status: 'tools.change_ticket_status',
};

// Status family → dot tone (the dot carries the meaning, bars stay neutral).
const STATUS_TONE = {
  'status-resolved': 'success',
  'status-closed': 'neutral',
  'status-waiting': 'warning',
  'status-pending': 'warning',
  'status-automated': 'accent',
  'status-processing': 'info',
  'status-open': 'info',
  'status-new': 'info',
};

// maxBarThickness is a dataset option in chart.js 4: the theme's element
// default does not reach the bars, so each dataset carries the kit's 24 px.
const BAR_MAX = 24;
const STATUS_ROWS = 7;
const BREAKDOWN_ROWS = 8;

// label · count · bar (relative to the largest row) · share of the scope.
const StatRows = ({ rows, total, lang, emptyText }) => {
  if (!rows.length) return <p className="statistics-v2__empty-line">{emptyText}</p>;
  const max = Math.max(1, ...rows.map((row) => row.count || 0));
  return (
    <ul className="statistics-v2__rows">
      {rows.map((row) => (
        <li key={row.key} className={`statistics-v2__row${row.muted ? ' is-muted' : ''}`} title={row.title || row.label}>
          <span className="statistics-v2__row-label">
            {row.tone && <span className={`statistics-v2__dot statistics-v2__dot--${row.tone}`} aria-hidden="true" />}
            <span className="statistics-v2__row-text">
              <span className="statistics-v2__row-name">{row.label}</span>
              {row.sub && <span className="statistics-v2__row-sub">{row.sub}</span>}
            </span>
          </span>
          <span className="statistics-v2__row-count u-num">{formatNumber(row.count, lang)}</span>
          <Meter value={row.count || 0} max={max} label={row.label} />
          <span className="statistics-v2__row-pct u-num">{total > 0 ? formatPercent(((row.count || 0) / total) * 100, lang) : '—'}</span>
        </li>
      ))}
    </ul>
  );
};

const Legend = ({ items }) => (
  <div className="u-legend">
    {items.map((item) => (
      <span className="u-legend__item" key={item.label}>
        <span className={`u-legend__swatch statistics-v2__swatch--${item.swatch}`} aria-hidden="true" />
        {item.label}
      </span>
    ))}
  </div>
);

const Statistics = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language || 'fr';
  const tp = (key, one, other, options) => t(key, options.count === 1 ? one : other, options);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = readStatsParams(searchParams);
  const query = overviewQuery(params);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [overview, setOverview] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState(null); // null = not loaded yet
  const [carrierOptions, setCarrierOptions] = useState([]);
  const requestSeq = useRef(0);

  useEffect(() => {
    axios.get('/api/brands/list', { headers: authHeaders() })
      .then((res) => setBrands(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching brands:', err));
    axios.get('/api/logistics/clients-edn', { headers: authHeaders() })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setClients([...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')));
      })
      .catch((err) => {
        console.error('Error fetching clients:', err); // the client select stays hidden
        setClients([]);
      });
    axios.get('/api/statistics/carriers?limit=100', { headers: authHeaders() })
      .then((res) => {
        const list = res.data?.carriers || [];
        setCarrierOptions([...list].sort((a, b) => (b.count || 0) - (a.count || 0)));
      })
      .catch((err) => console.error('Error fetching carriers:', err));
  }, []);

  const fetchStatistics = useCallback(async () => {
    const seq = ++requestSeq.current;
    setOverview(null);
    setError(null);
    try {
      const response = await axios.get(`/api/statistics/overview?${query}`, { headers: authHeaders() });
      if (seq === requestSeq.current) setOverview(response.data || {});
    } catch (err) {
      console.error('Error fetching statistics:', err);
      if (seq === requestSeq.current) {
        setError(t('statisticsPage.fetchError', 'Impossible de charger les statistiques.'));
        setOverview({});
      }
    }
  }, [query, t]);

  useEffect(() => { fetchStatistics(); }, [fetchStatistics]);

  // ------------------------------------------------------------ labels

  // « 28 oct. » — the shared short format (a day key stays that day).
  const fmtDay = useCallback(
    (key) => formatShortDate(String(key).slice(0, 10), lang, { empty: String(key) }),
    [lang],
  );

  const periodLabel = customRangeReady(params)
    ? t('statisticsPage.customPeriodLabel', 'du {{start}} au {{end}}', { start: fmtDay(params.start), end: fmtDay(params.end) })
    : {
      7: t('statisticsPage.last7Days', '7 derniers jours'),
      30: t('statisticsPage.last30Days', '30 derniers jours'),
      90: t('statisticsPage.last90Days', '90 derniers jours'),
      365: t('statisticsPage.lastYear', '12 derniers mois'),
    }[params.days === 'custom' ? '30' : params.days];

  const clientDoc = (clients || []).find((c) => (c.id || c._id) === params.client);
  const clientName = clientDoc?.name || null;
  const brandName = brands.find((b) => (b.id || b._id) === params.brand)?.name || null;
  const filtered = Boolean(params.client || params.carrier || params.brand);

  // ------------------------------------------------------------ data

  const loading = overview === null;
  const perDay = overview?.tickets_per_day;
  const totalTickets = typeof perDay?.total_tickets === 'number' ? perDay.total_tickets : null;
  const resolution = overview?.resolution;
  const responseTime = overview?.response_time;
  const sentiment = overview?.sentiment;
  const aiTools = overview?.ai_tools;
  const toolsPerTicket = overview?.tools_per_ticket;
  const aiRuns = overview?.ai_runs;
  const motifs = overview?.motifs;
  const matrix = overview?.motif_carrier_matrix;
  const carrierRows = overview?.carriers?.carriers || [];
  const carrierPerf = overview?.carrier_performance?.carriers || [];
  const topClients = overview?.top_clients;
  const topClientRows = topClients?.clients || [];
  const brandRows = useMemo(
    () => mergeBrandRows(overview?.brands?.brands, t('statisticsPage.noBrand', 'Sans marque')),
    [overview, t],
  );

  const showClientsTab = (!params.client && topClientRows.length > 0) || (!params.brand && brandRows.length > 1);
  const view = params.view === 'clients' && !showClientsTab ? 'activity' : params.view;

  // ------------------------------------------------------------ activity chart

  const series = useMemo(() => {
    const days = fillDays(perDay?.daily_data, perDay?.period_start, perDay?.period_end);
    const granularity = granularityFor(days);
    const buckets = bucketSeries(days, granularity);
    const cap = outlierCap(buckets.map((b) => b.total));
    return { buckets, granularity, cap, clipped: cap ? buckets.filter((b) => b.total > cap) : [] };
  }, [perDay]);

  const bucketLabel = useCallback((key) => (series.granularity === 'week'
    ? t('teamStatsUi.stats.weekOf', 'sem. du {{date}}', { date: fmtDay(key) })
    : fmtDay(key)), [series.granularity, fmtDay, t]);

  if (!loading && totalTickets > 0) ensureChartTheme();

  const activityChart = useMemo(() => {
    if (!series.buckets.length) return null;
    const open = series.buckets.map((b) => Math.max(0, b.total - b.resolved));
    const surface = cssToken('--bg-surface', '#FFFFFF');
    return {
      data: {
        labels: series.buckets.map((b) => b.key),
        datasets: [
          {
            label: t('teamStatsUi.stats.seriesResolved', 'Résolus'),
            data: series.buckets.map((b) => b.resolved),
            backgroundColor: seriesColor(0),
            maxBarThickness: BAR_MAX,
            stack: 'tickets',
            borderSkipped: 'start',
            borderRadius: (ctx) => (open[ctx.dataIndex] > 0 ? 0 : { topLeft: 4, topRight: 4 }),
          },
          {
            label: t('teamStatsUi.stats.seriesOpen', 'Pas encore résolus'),
            data: open,
            backgroundColor: seriesWash(0),
            maxBarThickness: BAR_MAX,
            stack: 'tickets',
            borderSkipped: false,
            borderColor: surface,
            borderWidth: (ctx) => ({ bottom: series.buckets[ctx.dataIndex]?.resolved > 0 ? 2 : 0, top: 0, left: 0, right: 0 }),
            borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => (items.length ? bucketLabel(items[0].label) : ''),
              label: (item) => `${item.dataset.label} : ${formatNumber(item.parsed.y, lang)}`,
              footer: (items) => (items.length
                ? t('teamStatsUi.stats.tooltipReceived', 'Reçus : {{count}}', { count: formatNumber(series.buckets[items[0].dataIndex]?.total, lang) })
                : ''),
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 10,
              callback(value) { return bucketLabel(this.getLabelForValue(value)); },
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            border: { display: false },
            ticks: { precision: 0, padding: 8 },
            ...(series.cap ? { max: series.cap } : {}),
          },
        },
      },
    };
  }, [series, bucketLabel, lang, t]);

  // ------------------------------------------------------------ motif charts

  const motifRows = useMemo(() => (motifs?.motifs || []).slice(0, 12), [motifs]); // API order: volume desc
  const motifChart = useMemo(() => {
    if (!motifRows.length) return null;
    return {
      data: {
        labels: motifRows.map((m) => m.motif),
        datasets: [{ data: motifRows.map((m) => m.count), backgroundColor: seriesColor(0), maxBarThickness: BAR_MAX }],
      },
      options: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => t('teamStatsUi.stats.motifTooltip', '{{count}} tickets · {{share}} des tickets avec un motif', {
                count: formatNumber(item.parsed.x, lang), share: formatPercent(motifRows[item.dataIndex]?.percentage, lang),
              }),
            },
          },
        },
        scales: {
          x: { beginAtZero: true, border: { display: false }, ticks: { precision: 0 } },
          y: { grid: { display: false }, border: { display: false } },
        },
      },
    };
  }, [motifRows, lang, t]);

  const matrixRows = useMemo(() => matrix?.rows || [], [matrix]);
  const matrixNames = useMemo(() => matrix?.motifs || [], [matrix]);
  const { slots: motifSlots, other: matrixOther } = useMemo(() => matrixSlots(matrix), [matrix]);
  const matrixChart = useMemo(() => {
    if (!matrixRows.length || !matrixNames.length) return null;
    const otherColor = cssToken('--border-strong', '#D8D7E4');
    const surface = cssToken('--bg-surface', '#FFFFFF');
    // The value end of a stacked bar is its last non-empty segment.
    const lastSegment = matrixRows.map((row) => {
      let last = -1;
      matrixNames.forEach((name, i) => { if (matrixValue(row, name, matrixOther) > 0) last = i; });
      return last;
    });
    return {
      data: {
        labels: matrixRows.map((r) => r.carrier),
        datasets: matrixNames.map((name, i) => ({
          label: name === matrixOther ? t('teamStatsUi.stats.others', 'Autres motifs') : name,
          data: matrixRows.map((r) => matrixValue(r, name, matrixOther)),
          backgroundColor: motifSlots[name] === 'other' ? otherColor : seriesColor(motifSlots[name]),
          maxBarThickness: BAR_MAX,
          borderColor: surface,
          borderWidth: { right: 2, left: 0, top: 0, bottom: 0 },
          borderSkipped: false,
          borderRadius: (ctx) => (lastSegment[ctx.dataIndex] === i ? { topRight: 4, bottomRight: 4 } : 0),
        })),
      },
      options: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        responsive: true,
        interaction: { intersect: false, mode: 'y' },
        plugins: {
          legend: { display: false },
          tooltip: { mode: 'y', intersect: false, filter: (item) => item.parsed.x > 0 },
        },
        scales: {
          x: { stacked: true, beginAtZero: true, border: { display: false }, ticks: { precision: 0 } },
          y: { stacked: true, grid: { display: false }, border: { display: false } },
        },
      },
    };
  }, [matrixRows, matrixNames, matrixOther, motifSlots, t]);

  const histogram = useMemo(() => toolsHistogram(toolsPerTicket?.distribution), [toolsPerTicket]);
  const histogramChart = useMemo(() => {
    if (histogram.length < 2) return null;
    return {
      data: {
        labels: histogram.map((b) => b.label),
        datasets: [{ data: histogram.map((b) => b.count), backgroundColor: seriesColor(0), maxBarThickness: BAR_MAX }],
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => (items.length ? t('teamStatsUi.stats.toolsCount', '{{tools}} outil(s) appelé(s)', { tools: items[0].label }) : ''),
              label: (item) => t('teamStatsUi.stats.ticketsCount', '{{count}} tickets', { count: formatNumber(item.parsed.y, lang) }),
            },
          },
        },
        scales: {
          x: { grid: { display: false }, border: { display: false } },
          y: { beginAtZero: true, border: { display: false }, ticks: { precision: 0 } },
        },
      },
    };
  }, [histogram, lang, t]);

  // ------------------------------------------------------------ header

  const scopeNames = [
    clientName || (params.client ? t('teamStatsUi.stats.thisClient', 'client choisi') : null),
    params.carrier || null,
    brandName,
  ].filter(Boolean);
  const subtitle = [
    !loading && totalTickets !== null
      ? tp('teamStatsUi.stats.subtitleCount', '{{formatted}} ticket reçu', '{{formatted}} tickets reçus', { count: totalTickets, formatted: formatNumber(totalTickets, lang) })
      : null,
    periodLabel,
    ...scopeNames,
  ].filter(Boolean).join(' · ');

  const clearFilters = () => updateParams({ client: null, carrier: null, brand: null });

  // ------------------------------------------------------------ zones

  const renderFilters = () => (
    <div className="statistics-v2__filters">
      <div className="statistics-v2__period">
        <Segmented
          label={t('statisticsPage.period', 'Période')}
          value={params.days}
          onChange={(value) => updateParams({ days: value === '30' ? null : value })}
          options={[
            { value: '7', label: t('teamStatsUi.stats.period7', '7 jours') },
            { value: '30', label: t('teamStatsUi.stats.period30', '30 jours') },
            { value: '90', label: t('teamStatsUi.stats.period90', '90 jours') },
            { value: '365', label: t('teamStatsUi.stats.period365', '12 mois') },
            { value: 'custom', label: t('statisticsPage.custom', 'Personnalisée') },
          ]}
        />
        {params.days === 'custom' && (
          <div className="statistics-v2__dates">
            <label className="statistics-v2__date">
              <span>{t('statisticsPage.startDate', 'Début')}</span>
              <input className="u-input" type="date" value={params.start} max={params.end || undefined} onChange={(e) => updateParams({ start: e.target.value })} />
            </label>
            <label className="statistics-v2__date">
              <span>{t('statisticsPage.endDate', 'Fin')}</span>
              <input className="u-input" type="date" value={params.end} min={params.start || undefined} onChange={(e) => updateParams({ end: e.target.value })} />
            </label>
          </div>
        )}
      </div>
      <div className="statistics-v2__selects">
        {(clients || []).length > 0 && (
          <select
            className="u-select"
            aria-label={t('statisticsPage.client', 'Client')}
            value={params.client}
            onChange={(e) => updateParams({ client: e.target.value })}
          >
            <option value="">{t('statisticsPage.allClients', 'Tous les clients')}</option>
            {clients.map((client) => (
              <option key={client.id || client._id} value={client.id || client._id}>{client.name}</option>
            ))}
            {params.client && !clientDoc && <option value={params.client}>{t('teamStatsUi.stats.unknownClient', 'Client introuvable')}</option>}
          </select>
        )}
        {(carrierOptions.length > 0 || params.carrier) && (
          <select
            className="u-select"
            aria-label={t('statisticsPage.carrier', 'Transporteur')}
            value={params.carrier}
            onChange={(e) => updateParams({ carrier: e.target.value })}
          >
            <option value="">{t('statisticsPage.allCarriers', 'Tous les transporteurs')}</option>
            {carrierOptions.map((c) => <option key={c.carrier} value={c.carrier}>{c.carrier}</option>)}
            {/* keep an unknown URL value selectable so it never silently disappears */}
            {params.carrier && !carrierOptions.some((c) => c.carrier === params.carrier) && (
              <option value={params.carrier}>{params.carrier}</option>
            )}
          </select>
        )}
        {brands.length > 0 && (
          <select
            className="u-select"
            aria-label={t('statisticsPage.brand', 'Marque')}
            value={params.brand}
            onChange={(e) => updateParams({ brand: e.target.value })}
          >
            <option value="">{t('statisticsPage.allBrands', 'Toutes les marques')}</option>
            {brands.map((brand) => <option key={brand.id || brand._id} value={brand.id || brand._id}>{brand.name}</option>)}
          </select>
        )}
      </div>
    </div>
  );

  // The scope in words: every section of the overview applies the same
  // filters (Backend _build_filter_clauses + carrier_filter_clause).
  const renderScope = () => {
    if (!filtered) return null;
    const unknownClient = Boolean(params.client && clients && clients.length > 0 && !clientDoc);
    const parts = [];
    if (params.client) {
      parts.push(clientName
        ? t('teamStatsUi.stats.scopeClient', 'les tickets rattachés au client {{name}} (par son identifiant, son nom ou ses alias)', { name: clientName })
        : t('teamStatsUi.stats.scopeClientAnon', 'les tickets rattachés au client choisi'));
    }
    if (params.carrier) {
      parts.push(t('teamStatsUi.stats.scopeCarrier', 'les tickets dont le transporteur est {{name}}, tous services confondus', { name: params.carrier }));
    }
    if (params.brand) {
      parts.push(brandName
        ? t('teamStatsUi.stats.scopeBrand', 'les tickets de la marque {{name}}', { name: brandName })
        : t('teamStatsUi.stats.scopeBrandAnon', 'les tickets de la marque choisie'));
    }
    return (
      <Banner
        tone={unknownClient ? 'warning' : 'info'}
        title={unknownClient
          ? t('teamStatsUi.stats.scopeUnknownClient', 'Ce client n’existe plus dans votre compte : aucun ticket ne lui correspond.')
          : t('teamStatsUi.stats.scopeTitle', 'Statistiques filtrées : {{names}}', { names: scopeNames.join(' · ') })}
        actions={(
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={clearFilters}>
            <X aria-hidden="true" />
            {t('teamStatsUi.stats.clearFilters', 'Voir tout le compte')}
          </button>
        )}
      >
        {t('teamStatsUi.stats.scopeBody', 'Chaque chiffre de cette page ne compte que {{parts}}.', { parts: parts.join(t('teamStatsUi.stats.and', ' et ')) })}
      </Banner>
    );
  };

  const renderSkeleton = () => (
    <>
      <div className="u-kpi-row" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="u-skeleton statistics-v2__kpi-skeleton" />)}
      </div>
      <span className="u-skeleton statistics-v2__chart-skeleton" aria-hidden="true" />
    </>
  );

  const renderKpis = () => {
    const resolved = resolution?.resolved_tickets;
    const responseSplit = responseTime?.tickets_analyzed > 0 ? splitMinutes(responseTime.avg_response_time_minutes, lang) : null;
    const medianSplit = splitHours(resolution?.median_resolution_hours, lang);
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={Inbox}
          label={t('teamStatsUi.stats.kpiTickets', 'Tickets reçus')}
          value={formatNumber(totalTickets, lang)}
          context={typeof perDay?.average_per_day === 'number' && perDay.average_per_day > 0
            ? t('teamStatsUi.stats.kpiTicketsContext', '≈ {{avg}} par jour d’activité', { avg: formatDecimal(perDay.average_per_day, lang) })
            : periodLabel}
          onClick={() => updateParams({ view: null })}
          title={t('teamStatsUi.stats.openActivity', 'Voir l’activité jour par jour')}
        />
        {typeof resolved === 'number' && resolution.total_tickets > 0 && (
          <Kpi
            icon={Gauge}
            tone="success"
            label={t('teamStatsUi.stats.kpiResolved', 'Résolus')}
            value={formatNumber(resolved, lang)}
            context={t('teamStatsUi.stats.kpiResolvedContext', '{{rate}} des tickets reçus', { rate: formatPercent(resolution.resolution_rate, lang) })}
            meter={{ value: resolution.resolution_rate || 0, max: 100, tone: 'success', label: t('teamStatsUi.stats.kpiResolved', 'Résolus') }}
          />
        )}
        {typeof resolution?.ai_share_of_resolutions === 'number' && resolved > 0 && (
          <Kpi
            icon={Sparkles}
            label={t('teamStatsUi.stats.kpiAiShare', 'Part de l’IA')}
            value={formatNumber(Math.round(resolution.ai_share_of_resolutions), lang)}
            unit="%"
            context={tp('teamStatsUi.stats.kpiAiContext', '{{formatted}} ticket résolu par l’IA', '{{formatted}} tickets résolus par l’IA', {
              count: resolution.ai_resolved_tickets || 0, formatted: formatNumber(resolution.ai_resolved_tickets, lang),
            })}
            meter={{ value: resolution.ai_share_of_resolutions, max: 100, label: t('teamStatsUi.stats.kpiAi', 'Résolus par l’IA') }}
            onClick={() => updateParams({ view: 'ai' })}
            title={t('teamStatsUi.stats.openAi', 'Voir le travail de l’agent IA')}
          />
        )}
        {medianSplit && (
          <Kpi
            icon={Timer}
            tone="info"
            label={t('teamStatsUi.stats.kpiResolution', 'Résolution')}
            value={medianSplit.value}
            unit={medianSplit.unit}
            context={t('teamStatsUi.stats.kpiResolutionContext', 'délai médian · moyenne {{avg}}', {
              avg: joinDuration(splitHours(resolution.avg_resolution_hours, lang)),
            })}
            title={t('teamStatsUi.stats.kpiResolutionTitle', 'Mesuré sur {{count}} tickets, de la création à la résolution', {
              count: formatNumber(resolution.tickets_with_resolution_time, lang),
            })}
          />
        )}
        {responseSplit && (
          <Kpi
            icon={Clock}
            tone="info"
            label={t('teamStatsUi.stats.kpiResponse', '1re réponse')}
            value={responseSplit.value}
            unit={responseSplit.unit}
            context={t('teamStatsUi.stats.kpiResponseContext', 'moyenne sur {{count}} tickets', { count: formatNumber(responseTime.tickets_analyzed, lang) })}
          />
        )}
      </div>
    );
  };

  const otherRow = (tail) => ({
    key: '__other',
    label: t('statisticsPage.othersRollup', 'Autres ({{total}})', { total: tail.size }),
    count: tail.count,
    muted: true,
  });

  const renderActivity = () => {
    const statuses = mergeStatusRows(overview?.status?.statuses, normalizeTicketStatus);
    const statusTotal = overview?.status?.total_tickets || 0;
    const { head: statusHead, tail: statusTail } = rollup(statuses, STATUS_ROWS);
    const statusRows = statusHead.map((row) => ({
      key: row.status,
      label: getStatusLabel(t, row.status),
      count: row.count,
      tone: STATUS_TONE[getStatusClass(row.status)] || 'info',
    }));
    if (statusTail) statusRows.push(otherRow(statusTail));
    const channels = (overview?.channels?.channels || []).map((c) => ({ ...c, count: c.ticket_count }));
    const { head: channelHead, tail: channelTail } = rollup(channels, BREAKDOWN_ROWS);
    const channelRows = channelHead.map((c) => ({
      key: c.channel_id || c.channel_name,
      label: c.channel_name,
      count: c.ticket_count,
      sub: typeof c.automation_rate === 'number'
        ? t('teamStatsUi.stats.channelAi', '{{rate}} résolus par l’IA', { rate: formatPercent(c.automation_rate, lang) })
        : null,
    }));
    if (channelTail) channelRows.push(otherRow(channelTail));
    const buckets = ['Positive', 'Neutral', 'Negative']
      .map((key) => (sentiment?.distribution || []).find((s) => s.bucket === key))
      .filter((b) => b && b.count > 0);
    const sentimentLabels = {
      Positive: t('statisticsPage.sentimentPositive', 'Positif'),
      Neutral: t('statisticsPage.sentimentNeutral', 'Neutre'),
      Negative: t('statisticsPage.sentimentNegative', 'Négatif'),
    };
    const weekly = series.granularity === 'week';
    return (
      <div className="statistics-v2__stack">
        {activityChart && (
          <Card
            icon={ChartColumn}
            title={weekly
              ? t('teamStatsUi.stats.activityWeekly', 'Tickets reçus par semaine')
              : t('teamStatsUi.stats.activityDaily', 'Tickets reçus par jour')}
            description={t('teamStatsUi.stats.activityDesc', '{{period}} · la partie pleine de chaque barre est déjà résolue', { period: periodLabel })}
            actions={<Legend items={[{ label: t('teamStatsUi.stats.seriesResolved', 'Résolus'), swatch: 's1' }, { label: t('teamStatsUi.stats.seriesOpen', 'Pas encore résolus'), swatch: 'w1' }]} />}
          >
            <div className="statistics-v2__chart statistics-v2__chart--activity">
              <Bar data={activityChart.data} options={activityChart.options} />
            </div>
            {series.clipped.length > 0 && (
              <p className="statistics-v2__note">
                {series.clipped
                  .map((b) => (weekly
                    ? t('teamStatsUi.stats.clippedWeek', 'pic de la semaine du {{date}} hors échelle : {{count}} tickets', { date: fmtDay(b.key), count: formatNumber(b.total, lang) })
                    : t('teamStatsUi.stats.clipped', 'pic du {{date}} hors échelle : {{count}} tickets', { date: fmtDay(b.key), count: formatNumber(b.total, lang) })))
                  .join(' · ')}
              </p>
            )}
          </Card>
        )}
        <div className="u-grid-2">
          {statusRows.length > 0 && (
            <Card
              title={t('statisticsPage.statusTitle', 'Statuts')}
              description={t('teamStatsUi.stats.statusDesc', 'Où en sont aujourd’hui les {{count}} tickets reçus sur la période', { count: formatNumber(statusTotal, lang) })}
            >
              <StatRows rows={statusRows} total={statusTotal} lang={lang} emptyText={t('statisticsPage.noData', 'Aucune donnée sur la période.')} />
            </Card>
          )}
          {channelRows.length > 0 && (
            <Card
              title={t('statisticsPage.channelsTitle', 'Canaux')}
              description={t('teamStatsUi.stats.channelsDesc', 'Par où les {{count}} tickets sont arrivés', { count: formatNumber(overview.channels.total_tickets, lang) })}
            >
              <StatRows rows={channelRows} total={overview.channels.total_tickets} lang={lang} emptyText={t('statisticsPage.noData', 'Aucune donnée sur la période.')} />
            </Card>
          )}
        </div>
        {buckets.length > 0 && sentiment.total_tickets > 0 && (
          <Card
            icon={Smile}
            iconTone={sentimentTone(sentiment.overall_average)}
            title={t('statisticsPage.sentimentTitle', 'Sentiment client')}
            badge={<Badge tone={sentimentTone(sentiment.overall_average)}>{t('teamStatsUi.stats.sentimentAvg', 'moyenne {{score}}/100', { score: Math.round(sentiment.overall_average) })}</Badge>}
            description={t('teamStatsUi.stats.sentimentDesc', 'Ton des messages clients sur {{count}} tickets analysés · négatif jusqu’à 30, neutre jusqu’à 60', { count: formatNumber(sentiment.total_tickets, lang) })}
          >
            <div className="u-stack statistics-v2__composition" role="img" aria-label={t('statisticsPage.sentimentTitle', 'Sentiment client')}>
              {buckets.map((b) => (
                <span key={b.bucket} className={`statistics-v2__seg--${b.bucket.toLowerCase()}`} style={{ width: `${(b.count / sentiment.total_tickets) * 100}%` }} />
              ))}
            </div>
            <div className="u-legend statistics-v2__composition-legend">
              {buckets.map((b) => (
                <span key={b.bucket} className="u-legend__item">
                  <span className={`u-legend__swatch statistics-v2__seg--${b.bucket.toLowerCase()}`} aria-hidden="true" />
                  {sentimentLabels[b.bucket]}
                  <b className="u-num">{formatNumber(b.count, lang)}</b>
                  <span className="u-num">{formatPercent(b.percentage, lang)}</span>
                </span>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  const serviceSummary = (services, max = 3) => {
    const list = services || [];
    if (!list.length || (list.length === 1 && !list[0].service)) return '';
    const parts = list.map((s) => `${s.service_label || t('statisticsPage.serviceUnspecified', 'service non précisé')} ${formatNumber(s.count, lang)}`);
    return parts.length > max ? `${parts.slice(0, max).join(' · ')} · …` : parts.join(' · ');
  };

  const scoreCell = (score) => (typeof score === 'number' ? (
    <span className="statistics-v2__score">
      <span className={`statistics-v2__dot statistics-v2__dot--${sentimentTone(score)}`} aria-hidden="true" />
      {Math.round(score)}
    </span>
  ) : '—');

  const renderMotifs = () => {
    const singleCarrier = params.carrier && carrierRows.length === 1 ? carrierRows[0] : null;
    const carrierTotal = overview?.carriers?.total_with_carrier || 0;
    const { head, tail } = rollup(carrierRows, BREAKDOWN_ROWS);
    const rows = singleCarrier
      ? (singleCarrier.services || []).map((s) => ({
        key: s.service || '__none',
        label: s.service_label || t('statisticsPage.serviceUnspecified', 'service non précisé'),
        count: s.count,
      }))
      : head.map((c) => ({
        key: c.carrier,
        label: c.carrier,
        count: c.count,
        sub: serviceSummary(c.services),
        title: [c.carrier, serviceSummary(c.services, 99)].filter(Boolean).join(' — '),
      }));
    if (!singleCarrier && tail) rows.push(otherRow(tail));
    const maxPerf = Math.max(1, ...carrierPerf.map((c) => c.total || 0));
    const legend = matrixNames.map((name) => ({
      label: name === matrixOther ? t('teamStatsUi.stats.others', 'Autres motifs') : name,
      swatch: motifSlots[name] === 'other' ? 'other' : `s${motifSlots[name] + 1}`,
    }));
    if (!matrixChart && !motifs?.total_tickets && !rows.length && !carrierPerf.length) {
      return (
        <Card>
          <EmptyState icon={Tag} title={t('teamStatsUi.stats.noMotifs', 'Aucun motif ni transporteur sur la période')}>
            {t('teamStatsUi.stats.noMotifsHint', 'Les motifs viennent de la classification des tickets ; le transporteur, du suivi du colis.')}
          </EmptyState>
        </Card>
      );
    }
    return (
      <div className="statistics-v2__stack">
        {matrixChart && (
          <Card
            icon={Truck}
            title={t('statisticsPage.matrixTitle', 'Motifs par transporteur')}
            description={t('teamStatsUi.stats.matrixDesc', '{{count}} tickets qui ont un transporteur et un motif · les 6 transporteurs et les 6 motifs les plus fréquents', { count: formatNumber(matrix.total_tickets, lang) })}
          >
            <Legend items={legend} />
            <div className="statistics-v2__chart statistics-v2__chart--matrix" style={{ height: Math.max(180, matrixRows.length * 40 + 36) }}>
              <Bar data={matrixChart.data} options={matrixChart.options} />
            </div>
          </Card>
        )}
        <div className="u-grid-2">
          {motifs && (
            <Card
              title={t('teamStatsUi.stats.motifsTitle', 'Motifs de contact')}
              description={motifs.total_tickets > 0
                ? t('teamStatsUi.stats.motifsDesc', '{{share}} des tickets ont un motif · {{count}} sans motif', {
                  share: formatPercent(motifs.coverage_percentage, lang), count: formatNumber(motifs.unclassified_tickets, lang),
                })
                : null}
            >
              {motifChart ? (
                <div className="statistics-v2__chart" style={{ height: Math.max(140, motifRows.length * 28 + 40) }}>
                  <Bar data={motifChart.data} options={motifChart.options} />
                </div>
              ) : <p className="statistics-v2__empty-line">{t('statisticsPage.noClassified', 'Aucun ticket classé sur la période.')}</p>}
            </Card>
          )}
          {rows.length > 0 && (
            <Card
              title={singleCarrier
                ? t('teamStatsUi.stats.servicesTitle', 'Services de {{name}}', { name: singleCarrier.carrier })
                : t('statisticsPage.carriersTitle', 'Transporteurs')}
              description={singleCarrier
                ? t('teamStatsUi.stats.servicesDesc', 'Le service tel qu’il est écrit sur les {{count}} tickets', { count: formatNumber(carrierTotal, lang) })
                : t('teamStatsUi.stats.carriersDesc', '{{count}} tickets ont un transporteur', { count: formatNumber(carrierTotal, lang) })}
            >
              <StatRows rows={rows} total={carrierTotal} lang={lang} emptyText={t('statisticsPage.noData', 'Aucune donnée sur la période.')} />
            </Card>
          )}
        </div>
        {carrierPerf.length > 0 && (
          <Card
            title={t('statisticsPage.carrierPerfTitle', 'Performance transporteurs')}
            description={t('teamStatsUi.stats.perfDesc', 'Les tickets de la période, par transporteur · délai mesuré de la création à la résolution')}
            flush
          >
            <div className="u-table-scroll">
              <table className="u-table statistics-v2__table">
                <thead>
                  <tr>
                    <th style={{ width: '24%' }}>{t('statisticsPage.colCarrier', 'Transporteur')}</th>
                    <th style={{ width: '19%' }}>{t('statisticsPage.colTickets', 'Tickets')}</th>
                    <th className="is-num" style={{ width: '11%' }}>{t('statisticsPage.colResolvedRate', '% résolus')}</th>
                    <th className="is-num" style={{ width: '11%' }}>{t('teamStatsUi.stats.colAiRate', '% par l’IA')}</th>
                    <th className="is-num" style={{ width: '13%' }}>{t('statisticsPage.colAvgResolution', 'Résolution moy.')}</th>
                    <th className="is-num" style={{ width: '10%' }}>{t('statisticsPage.colSentiment', 'Sentiment')}</th>
                    <th style={{ width: '12%' }} aria-label={t('teamStatsUi.actions', 'Actions')} />
                  </tr>
                </thead>
                <tbody>
                  {carrierPerf.map((c) => (
                    <tr key={c.carrier}>
                      <td title={[c.carrier, serviceSummary(c.services, 99)].filter(Boolean).join(' — ')}>
                        <b>{c.carrier}</b>
                        {serviceSummary(c.services) && <span className="u-cell-sub">{serviceSummary(c.services)}</span>}
                      </td>
                      <td>
                        <span className="statistics-v2__cell-meter">
                          <span className="u-num">{formatNumber(c.total, lang)}</span>
                          <Meter value={c.total || 0} max={maxPerf} label={c.carrier} />
                        </span>
                      </td>
                      <td className="is-num u-num">{formatPercent(c.resolution_rate, lang)}</td>
                      <td className="is-num u-num">{formatPercent(c.ai_resolution_rate, lang)}</td>
                      <td className="is-num u-num">{joinDuration(splitHours(c.avg_resolution_hours, lang))}</td>
                      <td className="is-num u-num">{scoreCell(c.avg_sentiment)}</td>
                      <td className="is-action">
                        {params.carrier !== c.carrier && (
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--sm"
                            onClick={() => updateParams({ carrier: c.carrier })}
                            title={t('teamStatsUi.stats.filterCarrier', 'Ne voir que {{name}}', { name: c.carrier })}
                          >
                            <Filter aria-hidden="true" />
                            {t('teamStatsUi.stats.filter', 'Filtrer')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderClients = () => {
    const maxClient = Math.max(1, ...topClientRows.map((c) => c.total || 0));
    return (
      <div className="statistics-v2__stack">
        {!params.client && topClientRows.length > 0 && (
          <Card
            icon={Building2}
            title={t('teamStatsUi.stats.topClientsTitle', 'Clients les plus actifs')}
            description={t('teamStatsUi.stats.topClientsDesc', 'Les {{shown}} clients qui ont le plus de tickets · {{count}} tickets ont un client', {
              shown: topClientRows.length, count: formatNumber(topClients.total_with_client, lang),
            })}
            flush
          >
            <div className="u-table-scroll">
              <table className="u-table statistics-v2__table">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>{t('statisticsPage.client', 'Client')}</th>
                    <th style={{ width: '19%' }}>{t('statisticsPage.colTickets', 'Tickets')}</th>
                    <th className="is-num" style={{ width: '10%' }}>{t('teamStatsUi.stats.colShare', 'Part')}</th>
                    <th className="is-num" style={{ width: '11%' }}>{t('statisticsPage.colResolvedRate', '% résolus')}</th>
                    <th className="is-num" style={{ width: '12%' }}>{t('teamStatsUi.stats.colAiResolved', 'Résolus IA')}</th>
                    <th className="is-num" style={{ width: '10%' }}>{t('statisticsPage.colSentiment', 'Sentiment')}</th>
                    <th style={{ width: '12%' }} aria-label={t('teamStatsUi.actions', 'Actions')} />
                  </tr>
                </thead>
                <tbody>
                  {topClientRows.map((c) => {
                    const id = clientIdFor(c.client, clients);
                    return (
                      <tr key={c.client}>
                        <td title={c.client}><b>{c.client}</b></td>
                        <td>
                          <span className="statistics-v2__cell-meter">
                            <span className="u-num">{formatNumber(c.total, lang)}</span>
                            <Meter value={c.total || 0} max={maxClient} label={c.client} />
                          </span>
                        </td>
                        <td className="is-num u-num">{formatPercent(c.percentage, lang)}</td>
                        <td className="is-num u-num">{formatPercent(c.resolution_rate, lang)}</td>
                        <td className="is-num u-num">{formatNumber(c.ai_resolved, lang)}</td>
                        <td className="is-num u-num">{scoreCell(c.avg_sentiment)}</td>
                        <td className="is-action">
                          {id && (
                            <button
                              type="button"
                              className="u-btn u-btn--ghost u-btn--sm"
                              onClick={() => updateParams({ client: id, view: null })}
                              title={t('teamStatsUi.stats.filterClient', 'Statistiques de {{name}}', { name: c.client })}
                            >
                              {t('teamStatsUi.stats.see', 'Voir')}
                              <ChevronRight aria-hidden="true" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        {!params.brand && brandRows.length > 1 && (
          <Card
            title={t('statisticsPage.brandsTitle', 'Marques')}
            description={t('teamStatsUi.stats.brandsDesc', '{{count}} tickets répartis par marque', { count: formatNumber(overview.brands.total_tickets, lang) })}
          >
            <StatRows
              rows={brandRows.map((b) => ({
                key: b.label,
                label: b.label,
                count: b.count,
                sub: t('teamStatsUi.stats.brandAi', '{{count}} résolus par l’IA', { count: formatNumber(b.aiResolved, lang) }),
              }))}
              total={overview.brands.total_tickets}
              lang={lang}
              emptyText={t('statisticsPage.noData', 'Aucune donnée sur la période.')}
            />
          </Card>
        )}
      </div>
    );
  };

  const renderAi = () => {
    const tools = aiTools?.tools || [];
    const facts = [
      typeof resolution?.ai_resolved_tickets === 'number' && resolution.resolved_tickets > 0 && {
        label: t('teamStatsUi.stats.aiResolved', 'Tickets résolus par l’IA'),
        value: t('teamStatsUi.stats.aiResolvedValue', '{{count}} · {{share}} des résolus', {
          count: formatNumber(resolution.ai_resolved_tickets, lang), share: formatPercent(resolution.ai_share_of_resolutions, lang),
        }),
      },
      aiRuns?.tickets_with_runs > 0 && typeof aiRuns.llm_calls_per_ticket === 'number' && {
        label: t('teamStatsUi.stats.llmPerTicket', 'Appels au modèle par ticket'),
        value: t('teamStatsUi.stats.llmPerTicketValue', '{{calls}} · sur {{count}} tickets tracés ({{coverage}})', {
          calls: formatDecimal(aiRuns.llm_calls_per_ticket, lang, 1),
          count: formatNumber(aiRuns.tickets_with_runs, lang),
          coverage: formatPercent(aiRuns.coverage_percentage, lang),
        }),
      },
      aiRuns?.tickets_with_runs > 0 && typeof aiRuns.cost_usd_per_ticket === 'number' && {
        label: t('teamStatsUi.stats.costPerTicket', 'Coût du modèle par ticket'),
        value: `≈ ${formatDecimal(aiRuns.cost_usd_per_ticket, lang, 4)} $`,
      },
      aiRuns?.runs > 0 && {
        label: t('teamStatsUi.stats.runs', 'Passages de l’agent'),
        value: aiRuns.failed_runs > 0
          ? t('teamStatsUi.stats.runsFailed', '{{runs}} · dont {{failed}} en échec', { runs: formatNumber(aiRuns.runs, lang), failed: formatNumber(aiRuns.failed_runs, lang) })
          : formatNumber(aiRuns.runs, lang),
      },
      aiRuns?.runs > 0 && typeof aiRuns.errors === 'number' && {
        label: t('teamStatsUi.stats.errors', 'Erreurs pendant les passages'),
        value: formatNumber(aiRuns.errors, lang),
      },
      toolsPerTicket?.total_tickets > 0 && typeof toolsPerTicket.average_tools_per_ticket === 'number' && {
        label: t('teamStatsUi.stats.toolsAvg', 'Outils par ticket'),
        value: t('teamStatsUi.stats.toolsAvgValue', '{{avg}} en moyenne', { avg: formatDecimal(toolsPerTicket.average_tools_per_ticket, lang, 2) }),
      },
    ].filter(Boolean);
    if (!facts.length && !tools.length) {
      return (
        <Card>
          <EmptyState icon={Bot} title={t('teamStatsUi.stats.noAi', 'L’agent IA n’a rien traité sur cette période')}>
            {t('teamStatsUi.stats.noAiHint', 'Les outils appelés et les passages de l’agent apparaissent ici dès qu’il traite des tickets.')}
          </EmptyState>
        </Card>
      );
    }
    return (
      <div className="statistics-v2__stack">
        {facts.length > 0 && (
          <Card
            icon={Sparkles}
            title={t('teamStatsUi.stats.aiTitle', 'L’agent IA sur la période')}
            description={t('teamStatsUi.stats.aiDesc', 'Passages tracés depuis le 16 sept. 2026 : les tickets plus anciens n’en ont pas.')}
          >
            <DescriptionList items={facts} />
          </Card>
        )}
        <div className="u-grid-2">
          {tools.length > 0 && (
            <Card
              title={t('statisticsPage.aiToolsTitle', 'Outils IA')}
              description={t('teamStatsUi.stats.toolsDesc', '{{count}} appels des {{shown}} outils les plus utilisés', {
                count: formatNumber(aiTools.total_tool_calls, lang), shown: tools.length,
              })}
            >
              <StatRows
                rows={tools.map((tool) => ({
                  key: tool.tool_name,
                  label: TOOL_LABELS[tool.tool_name] ? t(TOOL_LABELS[tool.tool_name], tool.tool_name) : tool.tool_name,
                  title: tool.tool_name,
                  count: tool.call_count,
                  sub: typeof tool.tickets_used_in === 'number'
                    ? t('statisticsPage.usedInTickets', 'dans {{total}} tickets', { total: formatNumber(tool.tickets_used_in, lang) })
                    : null,
                }))}
                total={aiTools.total_tool_calls}
                lang={lang}
                emptyText={t('statisticsPage.noData', 'Aucune donnée sur la période.')}
              />
            </Card>
          )}
          {histogramChart && (
            <Card
              title={t('statisticsPage.toolsPerTicketTitle', 'Outils utilisés par ticket')}
              description={t('teamStatsUi.stats.histogramDesc', 'Nombre de tickets selon le nombre d’outils appelés · moyenne {{avg}}', {
                avg: formatDecimal(toolsPerTicket.average_tools_per_ticket, lang, 2),
              })}
            >
              <div className="statistics-v2__chart statistics-v2__chart--histogram">
                <Bar data={histogramChart.data} options={histogramChart.options} />
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderEmpty = () => (
    <Card>
      <EmptyState
        icon={ChartColumn}
        title={t('teamStatsUi.stats.emptyTitle', 'Aucun ticket sur cette période')}
        action={(
          <div className="statistics-v2__empty-actions">
            {filtered && (
              <button type="button" className="u-btn u-btn--secondary" onClick={clearFilters}>
                {t('teamStatsUi.stats.clearFilters', 'Voir tout le compte')}
              </button>
            )}
            {params.days !== '365' && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => updateParams({ days: '365', start: null, end: null })}>
                {t('teamStatsUi.stats.seeYear', 'Voir les 12 derniers mois')}
              </button>
            )}
          </div>
        )}
      >
        {filtered
          ? t('teamStatsUi.stats.emptyFiltered', 'Aucun ticket ne correspond à ce filtre sur la période ({{period}}).', { period: periodLabel })
          : t('teamStatsUi.stats.emptyHint', 'Les statistiques se remplissent dès que vos canaux reçoivent des tickets.')}
      </EmptyState>
    </Card>
  );

  const renderBody = () => {
    if (loading) return renderSkeleton();
    if (error || totalTickets === null) return null;
    if (totalTickets === 0) return renderEmpty();
    const tabs = [
      { key: 'activity', label: t('teamStatsUi.stats.tabActivity', 'Activité'), icon: ChartColumn },
      { key: 'motifs', label: t('teamStatsUi.stats.tabMotifs', 'Motifs et transporteurs'), icon: Truck },
      showClientsTab && { key: 'clients', label: t('teamStatsUi.stats.tabClients', 'Clients et marques'), icon: Building2 },
      { key: 'ai', label: t('teamStatsUi.stats.tabAi', 'Agent IA'), icon: Sparkles },
    ].filter(Boolean);
    return (
      <>
        {renderKpis()}
        <Tabs
          label={t('statisticsPage.title', 'Statistiques')}
          tabs={tabs}
          active={view}
          onChange={(key) => updateParams({ view: key === 'activity' ? null : key })}
        />
        <div role="tabpanel">
          {view === 'activity' && renderActivity()}
          {view === 'motifs' && renderMotifs()}
          {view === 'clients' && renderClients()}
          {view === 'ai' && renderAi()}
        </div>
      </>
    );
  };

  return (
    <div className="u-page statistics-v2">
      <PageHeader
        title={t('statisticsPage.title', 'Statistiques')}
        subtitle={subtitle}
        actions={(
          <button type="button" className="u-btn u-btn--secondary" onClick={fetchStatistics}>
            <RefreshCw aria-hidden="true" />
            {t('statisticsPage.refresh', 'Actualiser')}
          </button>
        )}
      />
      <div className="u-well">
        {renderFilters()}
        {renderScope()}
        {error && (
          <Banner
            tone="danger"
            title={error}
            actions={(
              <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchStatistics}>
                {t('statisticsPage.retry', 'Réessayer')}
              </button>
            )}
          >
            {t('teamStatsUi.stats.errorHint', 'Les chiffres n’ont pas pu être calculés. Réessayez dans un instant.')}
          </Banner>
        )}
        {renderBody()}
      </div>
    </div>
  );
};

export default Statistics;
